import { ChangeDetectorRef, Component, inject, OnDestroy } from '@angular/core';
import { PageContainerComponent } from '../../layout/page-container/page-container.component';
import { BaseComponent } from '../base/base.component';
import { API_STATUS,ApiEnvNames,ERROR_RESPONSE,ExportConfig,SearchConfig} from '../../model/api.model';
import { OptionsFetchConfig } from '../../model/common.model';
import { DataTableSchema } from '../../model/dynamic-data-table.model';
import { DynamicFormArray,FIELD_TYPES,FormFieldConfig,Props} from '../../model/form.model';
import { CommonUtils } from '../../utils/common.utils';
import {DistributionListTableSchema,DistributionListFormArray,DistributionListProps,DistributionListDynamicOptionFields,DistributionListExportConfig,DistributionListSearchConfig} from '../../model/distribution-list.model';
import { CommonService } from '../../services/common.service';
import { GlobalApiService } from '../../services/global-api.service';
import { FieldNames } from '../../model/form-fields.model';
import { Subject, takeUntil } from 'rxjs';
import { CSV_EXTENSION,ExcelExportService,} from '../../services/excel-export.service';
import { PaginatorService } from '../../services/paginator-service.service';

@Component({
  selector: 'app-distribution-list',
  standalone: true,
  imports: [PageContainerComponent],
  templateUrl: './distribution-list.component.html',
})
export class DistributionListComponent extends BaseComponent implements OnDestroy {
  protected data = 'DistributionListComponent';
  public dataTableSchema: DataTableSchema = structuredClone(DistributionListTableSchema);
  public filterSchema: DynamicFormArray = structuredClone(DistributionListFormArray);
  protected props: Props = DistributionListProps;
  title = '';
  service = inject(CommonService);
  commonUtils = inject(CommonUtils);
  changeRef = inject(ChangeDetectorRef);
  paginatorService = inject(PaginatorService);
  excelService = inject(ExcelExportService);
  private isFirstLoad = true;
  isDataInitialized = false;
  currentPage: any;
  public tableFilter = '';
  readonly destroy$ = new Subject<void>();
  distributionListDynamicOptionFields: OptionsFetchConfig = DistributionListDynamicOptionFields;
  private readonly globalApi = inject(GlobalApiService);
  override ngOnInit() {
    super.ngOnInit();
    super.commonFunction();
    this.getData();
    this.dataTableSchema.filterCallback = this.paginationFilterCallback;
    this.dataTableSchema.exportCallback = this.exportCallback;
    this.dataTableSchema.sortCallback = this.sortCallback;
    this.paginatorService.pageEvent$.pipe(takeUntil(this.destroy$)).subscribe((event) => {
      if (this.isFirstLoad) {
        this.isFirstLoad = false;
        if (this.dataTableSchema['pageSizeOptions']['pageSize']) {
          this.dataTableSchema['pageSizeOptions']['pageLength'] = this.dataTableSchema['pageSizeOptions']['pageSize'][0];
        }
      } else {
        this.currentPage = event;
        this.dataTableSchema.dataSource = [];
        this.setTableLoadingState(true);
        this.onSearchClicked('');
      }
    });
  }
  override onSearchClicked = (searchParam: any) => {
    if (searchParam) {
      this.resetSorting()
      this.resetDataTable();
    }
    const searchConfig = this.getSearchConfig();
    this.fetchAllData(searchConfig);
  };

  exportCallback = () => {
    const searchConfig = this.getSearchConfig();
    searchConfig['body']['download'] = true;
    this.fetchAllData(searchConfig, true);
  };

  paginationFilterCallback = (filterValue: string) => {
    if (this.isDataInitialized) {
      if (filterValue || (!filterValue && this.tableFilter)) {
        const searchConfig = this.getSearchConfig();
        this.tableFilter = filterValue;
        this.resetDataTable();
        this.fetchAllData(searchConfig);
      }
    }
  };
  sortCallback = (fieldInfo: Array<Record<string, string>>) => {
    if (fieldInfo) {
      const sortBy: any = {}
      fieldInfo.forEach(field => {
        sortBy[field['id']] = field['direction']
      })
      this.sortBy = sortBy;
      if (this.dataTableSchema.dataSource.length > 0) {
        const searchConfig = this.getSearchConfig();
        this.resetDataTable()
        this.fetchAllData(searchConfig);
      }
    }
  }

  getSearchConfig = () => {
    const searchConfig = structuredClone(DistributionListSearchConfig);
    this.filterSchema['FieldsArray'].forEach((field) => {
      if (field['selectedValue'] &&field['selectedValue'] !== null &&field['selectedValue'] !== '') {
        if (Number.isNaN(field['selectedValue']) || Array.isArray(field['selectedValue'])) {
          searchConfig['body'][field['fieldName']] = field['selectedValue'];
        } else {
          searchConfig['body'][field['fieldName']] = field['selectedValue'].toString();
        }
      } else if (field['fieldType'] === FIELD_TYPES.NUMBER) {
        searchConfig['body'][field['fieldName']] = '';
      }
    });
    if (this.dataTableSchema.inputFilter.length > 0) {
      searchConfig['body']['searchText'] = this.dataTableSchema.inputFilter
    }
    if(this.sortBy && Object.keys(this.sortBy).length > 0 ) {
      if (!searchConfig['body']) {
        searchConfig['body'] = {}
      }
      searchConfig['body']['sortBy'] = this.sortBy;
    }
    return searchConfig;
  };

  getData = () => {
    this.service.getDataFromDataSource(this.distributionListDynamicOptionFields.searchConfig,ApiEnvNames['JobEnrollment']).then((response) => {
        if (response['status'] === API_STATUS['Success']) {
          if (Array.isArray(response.result)) {
            this.fillValuesFromApi(response.result[0]);
          }
        }
        this.setTableLoadingState(false);
      })
      .then(() => {
        this.getMySpecificData();
      })
      .catch((err) => {
        console.info(ERROR_RESPONSE['layoutErrorPrefix'], err); 
        this.setTableLoadingState(false);
      });
  };

  fillValuesFromApi = (response: any) => {
    if (response) {
      const fieldNames = new Set(this.distributionListDynamicOptionFields.fields.map((p) => p.name));
      this.filterSchema.FieldsArray.forEach((filterField) => {
        if (fieldNames.has(filterField['fieldName'])) {
          if (response[filterField['fieldName']]) {
            const fieldConfig =
              this.distributionListDynamicOptionFields.fields.find((p) => p.name === filterField['fieldName']); 
            if (!fieldConfig) {return};
            const keyValuePair = this.globalApi.mapFieldValuesForDropDowns(response[filterField['fieldName']],fieldConfig.keyValuePair);
            filterField['options'] = keyValuePair;
          }
          filterField['isLoading'] = false;
        }
      });
    } else {
      this.commonUtils.showMatSnackBar(ERROR_RESPONSE.someErrorOccured);
    }
  };
  override updateOptions = () => {};

  getMySpecificData = () => {
    this.filterSchema.FieldsArray.forEach((filterField) => {
      if ([FIELD_TYPES.DROPDOWN].includes(filterField['fieldType']) && filterField['options'] && filterField['options'].length <= 0) {
      this.globalService.getOptionsForField(filterField['fieldName']).then((options) => {
          filterField['options'] = options;
          filterField['isLoading'] = false;
        })
        .catch((err) => {
          console.info(err); 
          filterField['options'] = [];
          filterField['isLoading'] = false;
        });
      }
    });
  };
  fetchAllData = (searchParam: SearchConfig, callFromExport = false) => {
    this.setTableLoadingState(true);
    this.filterSchema['FieldsArray'].forEach((field) => {
      if (field['selectedValue'] &&field['selectedValue'] !== null &&field['selectedValue'] !== '') {
        if (field['fieldType'] === FIELD_TYPES.CHECKBOX) {
          searchParam['body'][field['fieldName']] =
            this.fetchValuesForPostCall(field);
        } else {
          searchParam['body'][field['fieldName']] = field['selectedValue'];
        }
      }
    });
    const certificationField = searchParam?.body?.[FieldNames.Certification];
    if (Array.isArray(certificationField) && certificationField.length === 0) {
      searchParam.body[FieldNames.Certification] = '';
    }
    if (!callFromExport) {
      searchParam['body'].page = Number(this.currentPage?.pageIndex ?? 0) + 1;
      searchParam['body'].pageSize = Number(this.currentPage?.pageSize);
    }
    this.service.getDataFromDataSource(searchParam, ApiEnvNames['JobEnrollment']).then(async (response) => {
      if(response['status'] === API_STATUS['Success'] && Array.isArray(response.result)) {
        if (!callFromExport) { 
          this.dataTableSchema['pageSizeOptions']['pageLength'] = response?.result?.[0]?.totalRecordsCount ?? 0;
          this.dataTableSchema.dataSource = response.result;
          if (!this.isDataInitialized && response.result.length > 0) {
            this.isDataInitialized = true;
          }
        } else {
          const exportConfig: ExportConfig = {
            tableSchema: this.dataTableSchema,
            environment: ApiEnvNames['StaffMaster'],
            fileExtension: CSV_EXTENSION,
            searchParameters: DistributionListExportConfig,
            exportFileName: 'Distribution_list',
          };
          await this.service.downloadExcelFromS3(response.result[0], exportConfig);
        }
      } else {
        this.dataTableSchema.dataSource = [];
        this.commonUtils.showMatSnackBar(ERROR_RESPONSE['errorFetch']);
      }
      this.setTableLoadingState(false);
      this.changeRef.detectChanges()
    })
    .catch((err) => {
      this.setTableLoadingState(false);
      this.dataTableSchema.dataSource = [];
      console.info(ERROR_RESPONSE['layoutErrorPrefix'], err);
    })
    .finally(() => {
      this.setTableLoadingState(false);
    });
  };

  extractKeysFromObject = (values: any) => {
    return Object.values(values).flat() as string[];
  };

  fetchValuesForPostCall = (param: FormFieldConfig) => {
    let returnValues: any[] = [];
    const values = this.extractKeysFromObject(param['selectedValue']);
    const distinctValues = [...new Set(this.filterSchema.FieldsArray.find((f) => f.fieldName === param.fieldName)?.options?.map((item) => item.fieldValue))];
    const allStartWithAll = values.every((val) => val.startsWith('All '));
    const sameLength = values.length === distinctValues.length;
    const result = allStartWithAll && sameLength;
    if (result) {
      return 'All';
    } else {
      returnValues = values.filter((p) => !p.startsWith('All '));
      values.forEach((p) => {
        if (p.startsWith('All')) {
          const fetchField = p.replace('All ', '');
          const matchingOptions =
          this.filterSchema.FieldsArray.find((f) => f.fieldName === param.fieldName)?.options?.filter((x) => x.fieldValue === fetchField).map((x) => x.fieldLabel) ?? [];
          returnValues = [...returnValues, ...matchingOptions];
        }
      });
      return returnValues;
    }
  };

  override onResetClicked = () => {
    this.filterSchema.FieldsArray = this.filterSchema.FieldsArray.map((field) => ({ ...field, selectedValue: field['defaultValue'] }));
    this.resetSorting()
    this.dataTableSchema.inputFilter = '';
    this.resetDataTable();
    this.checkForAnyEmptyFields();
    this.isDataInitialized = false;
  };

  resetDataTable() {
    this.dataTableSchema.dataSource = [];
    if (this.dataTableSchema?.pageSizeOptions) {
      if (this.dataTableSchema?.pageSizeOptions.pageSize) {
        this.currentPage = {
          page: 0,
          pageSize: Number(this.currentPage?.pageSize) || this.dataTableSchema?.pageSizeOptions.pageSize[0],
        };
      }
    }
    this.dataTableSchema['pageSizeOptions']['pageLength'] = 0;
  }
  
  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
